export type VisibilityFilter = 'ALL' | 'PUBLIC' | 'PRIVATE';

export interface VisibilityChange {
  adminName: string;
  isPublic: boolean;
  reason: string | null;
  createdAt: string;
}

export interface RestaurantRow {
  name: string;
  slug: string;
  ownerEmail: string | null;
  isPublic: boolean;
}

const normalize = ({ text }: { text: string }) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export function filterRestaurants<T extends RestaurantRow>({
  restaurants,
  search,
  visibility,
}: {
  restaurants: readonly T[];
  search: string;
  visibility: VisibilityFilter;
}): T[] {
  const term = normalize({ text: search });

  return restaurants.filter((restaurant) => {
    if (visibility === 'PUBLIC' && !restaurant.isPublic) return false;
    if (visibility === 'PRIVATE' && restaurant.isPublic) return false;
    if (!term) return true;

    const haystack = [restaurant.name, restaurant.slug, restaurant.ownerEmail ?? '']
      .map((text) => normalize({ text }))
      .join(' ');
    return haystack.includes(term);
  });
}

export function formatVisibilityChange({
  change,
}: {
  change: VisibilityChange | null;
}): string | null {
  if (!change) return null;

  const date = new Date(change.createdAt).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const action = change.isPublic ? 'Tornado público' : 'Tornado privado';
  const reason = change.reason ? ` · Motivo: ${change.reason}` : '';

  return `${action} por ${change.adminName} em ${date}${reason}`;
}

export function countByVisibility({
  restaurants,
}: {
  restaurants: readonly RestaurantRow[];
}): { total: number; publicCount: number; privateCount: number } {
  const privateCount = restaurants.filter(({ isPublic }) => !isPublic).length;

  return {
    total: restaurants.length,
    publicCount: restaurants.length - privateCount,
    privateCount,
  };
}
